import axios from 'axios'
import { API_BASE_URL_WITH_VERSION } from '../config/api'

// User Journey API Configuration
const userJourneyClient = axios.create({
  baseURL: `${API_BASE_URL_WITH_VERSION}/user-journey`,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Types
export interface UserSignupData {
  email: string
  name: string
  dateOfBirth: string
  timeOfBirth?: string
  placeOfBirth?: string
  gender?: string
}

export interface AstrologicalData {
  userId: string
  natalChart: any
  doshaProfile?: any
  externalApiData?: any
}

export interface YogaRequest {
  userId: string
  query: string
  response: any
  recommendations?: string[]
}

export interface CompatibilityCheck {
  userId: string
  person1: any
  person2: any
  analysis: any
  response: string
}

export interface ChatInteraction {
  userId: string
  message: string
  response: string
  context?: any
}

// User Journey API Functions
export const userJourneyAPI = {
  // Initialize Pinecone index
  initializeIndex: async () => {
    try {
      const response = await userJourneyClient.post('/init')
      return response.data
    } catch (error) {
      console.error('Error initializing user journey index:', error)
      return { success: false, error: 'Failed to initialize index' }
    }
  },

  // User signup
  signup: async (userData: UserSignupData) => {
    try {
      const response = await userJourneyClient.post('/signup', userData)
      return response.data
    } catch (error) {
      console.error('Error during user signup:', error)
      return { success: false, error: 'Failed to create user profile' }
    }
  },

  // Store astrological data
  storeAstrologicalData: async (astroData: AstrologicalData) => {
    try {
      const response = await userJourneyClient.post('/astrological-data', astroData)
      return response.data
    } catch (error) {
      console.error('Error storing astrological data:', error)
      return { success: false, error: 'Failed to store astrological data' }
    }
  },

  // Track yoga pose request
  trackYogaRequest: async (yogaRequest: YogaRequest) => {
    try {
      const response = await userJourneyClient.post('/yoga-request', yogaRequest)
      return response.data
    } catch (error) {
      console.error('Error tracking yoga request:', error)
      return { success: false, error: 'Failed to track yoga request' }
    }
  },

  // Track compatibility check
  trackCompatibilityCheck: async (compatibilityData: CompatibilityCheck) => {
    try {
      const response = await userJourneyClient.post('/compatibility-check', compatibilityData)
      return response.data
    } catch (error) {
      console.error('Error tracking compatibility check:', error)
      return { success: false, error: 'Failed to track compatibility check' }
    }
  },

  // Track chat interaction
  trackChatInteraction: async (chatData: ChatInteraction) => {
    try {
      const response = await userJourneyClient.post('/chat-interaction', chatData)
      return response.data
    } catch (error) {
      console.error('Error tracking chat interaction:', error)
      return { success: false, error: 'Failed to track chat interaction' }
    }
  },

  // Get user profile
  getUserProfile: async (userId: string) => {
    try {
      const response = await userJourneyClient.get(`/profile/${userId}`)
      return response.data
    } catch (error) {
      console.error('Error getting user profile:', error)
      return { success: false, error: 'Failed to get user profile' }
    }
  },

  // Get user interactions
  getUserInteractions: async (userId: string, limit: number = 50) => {
    try {
      const response = await userJourneyClient.get(`/interactions/${userId}?limit=${limit}`)
      return response.data
    } catch (error) {
      console.error('Error getting user interactions:', error)
      return { success: false, error: 'Failed to get user interactions' }
    }
  },

  // Get user journey summary
  getUserSummary: async (userId: string) => {
    try {
      const response = await userJourneyClient.get(`/summary/${userId}`)
      return response.data
    } catch (error) {
      console.error('Error getting user summary:', error)
      return { success: false, error: 'Failed to get user summary' }
    }
  },

  // Get user insights
  getUserInsights: async (userId: string) => {
    try {
      const response = await userJourneyClient.get(`/insights/${userId}`)
      return response.data
    } catch (error) {
      console.error('Error getting user insights:', error)
      return { success: false, error: 'Failed to get user insights' }
    }
  },

  // Search user journey
  searchUserJourney: async (userId: string, query: string, limit: number = 10) => {
    try {
      const response = await userJourneyClient.post(`/search/${userId}`, { query, limit })
      return response.data
    } catch (error) {
      console.error('Error searching user journey:', error)
      return { success: false, error: 'Failed to search user journey' }
    }
  },

  // Get similar users
  getSimilarUsers: async (userId: string, limit: number = 5) => {
    try {
      const response = await userJourneyClient.get(`/similar-users/${userId}?limit=${limit}`)
      return response.data
    } catch (error) {
      console.error('Error getting similar users:', error)
      return { success: false, error: 'Failed to get similar users' }
    }
  },

  // Update user profile
  updateUserProfile: async (userId: string, updates: any) => {
    try {
      const response = await userJourneyClient.put(`/profile/${userId}`, updates)
      return response.data
    } catch (error) {
      console.error('Error updating user profile:', error)
      return { success: false, error: 'Failed to update user profile' }
    }
  },

  // Get analytics
  getAnalytics: async () => {
    try {
      const response = await userJourneyClient.get('/analytics')
      return response.data
    } catch (error) {
      console.error('Error getting analytics:', error)
      return { success: false, error: 'Failed to get analytics' }
    }
  },

  // Delete user data
  deleteUserData: async (userId: string) => {
    try {
      const response = await userJourneyClient.delete(`/user/${userId}`)
      return response.data
    } catch (error) {
      console.error('Error deleting user data:', error)
      return { success: false, error: 'Failed to delete user data' }
    }
  },

  // Health check
  healthCheck: async () => {
    try {
      const response = await userJourneyClient.get('/health')
      return response.data
    } catch (error) {
      console.error('Error checking user journey health:', error)
      return { success: false, error: 'Failed to check health' }
    }
  },

  // Generate comprehensive report
  generateReport: async (userId: string, reportType: string = 'comprehensive', timeFrame: string = 'month', includeCharts: boolean = false) => {
    try {
      const response = await userJourneyClient.post('/report', {
        userId,
        reportType,
        timeFrame,
        includeCharts
      })
      return response.data
    } catch (error) {
      console.error('Error generating report:', error)
      return { success: false, error: 'Failed to generate report' }
    }
  },
}

export default userJourneyAPI 